import React, { useEffect, useRef, useState } from 'react';
import { addDays, isToday, subDays } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useClickAway } from 'react-use';
import { useDebouncedCallback } from 'use-debounce';
import { CalendarSelection, DatePickerWrapper, IconButton, Layout, RevealPickerButton } from './DateSelection.styles';
import SvgCalendar from '../../../../play-core/icons/Calendar';
import SvgChevron from '../../../../play-core/icons/Chevron';
import { ignoreTimezoneExtension } from '../../../../play-core/utils/date-helper';
import { useAnalyticsDispatch } from '../../analytics/AnalyticsContext';
import { useHydrated } from '../../hooks/use-hydrated';
import { useViewport } from '../../providers/ViewportProvider/ViewportContext';
import { isoDate, shortDateFormat } from '../../utils/date-formatter';
import { DatePicker } from '../DatePicker/DatePicker';
import { PlayContainer, defaultContainerPadding } from '../PlayContainer/PlayContainer';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
export const DateSelection = ({
  date,
  restrictedRange,
  analyticsPageId,
  onDateChange,
  onInstantChange
}) => {
  const {
    t
  } = useTranslation();
  const analytics = useAnalyticsDispatch();
  const [isPickerOpen, setPickerOpen] = useState(false);
  const [isPickerRevealed, setPickerReveal] = useState(false);
  const [internalDate, setInternalDate] = useState(date);
  const {
    isSmall
  } = useViewport();
  const isHydrated = useHydrated();
  const datePickerWrapperRef = useRef(null);
  useClickAway(datePickerWrapperRef, () => setPickerOpen(false));
  useEffect(() => setInternalDate(date), [date]);
  const selectDatePickerDate = selectedDate => {
    onDateChange(selectedDate);
    onInstantChange && onInstantChange(selectedDate);
    setPickerOpen(false);
  };
  const arrowDateChanges = d => {
    setInternalDate(d);
    onInstantChange && onInstantChange(d);
    debouncedDateChange(d);
  };
  const debouncedDateChange = useDebouncedCallback(d => {
    onDateChange(d);
  }, 300);
  const onOpenChanged = isOpen => {
    setPickerOpen(isOpen);
    setPickerReveal(prevState => prevState || isOpen);
  };
  const revealPickerButtonText = isHydrated && isToday(internalDate) ? `${t('global.today')}, ${shortDateFormat(internalDate)}` : shortDateFormat(internalDate);
  return /*#__PURE__*/_jsx(PlayContainer, {
    sidePadding: {
      ...defaultContainerPadding,
      default: 0
    },
    children: /*#__PURE__*/_jsx(Layout, {
      children: /*#__PURE__*/_jsxs(CalendarSelection, {
        children: [/*#__PURE__*/_jsx(IconButton, {
          type: "icon",
          expand: false,
          ariaLabel: t('videoByDate.previousDay'),
          icon: /*#__PURE__*/_jsx(SvgChevron, {
            orientation: 'left'
          }),
          onClick: () => arrowDateChanges(subDays(internalDate, 1)),
          tabIndex: -1
        }), /*#__PURE__*/_jsxs(DatePickerWrapper, {
          ref: datePickerWrapperRef,
          isPickerRevealed: isPickerRevealed,
          children: [!isPickerRevealed && /*#__PURE__*/_jsx(RevealPickerButton, {
            type: "text",
            expand: true,
            tabIndex: -1,
            text: revealPickerButtonText,
            icon: /*#__PURE__*/_jsx(SvgCalendar, {})
          }), /*#__PURE__*/_jsx(DatePicker, {
            fullWidth: isSmall,
            date: isoDate(internalDate),
            isOpen: isPickerOpen,
            onChange: selectedDate => {
              if (selectedDate) {
                selectDatePickerDate(new Date(selectedDate + ignoreTimezoneExtension));
                analytics({
                  type: 'click-event',
                  name: 'DateSelectionCalendarClick',
                  value1: selectedDate,
                  value2: analyticsPageId
                });
              }
            },
            onOpenChanged: onOpenChanged,
            fireChangesWhenUndefined: false,
            restrictedRange: restrictedRange,
            showCalendarIcon: true,
            expandInput: true
          })]
        }), /*#__PURE__*/_jsx(IconButton, {
          type: "icon",
          expand: false,
          ariaLabel: t('videoByDate.nextDay'),
          icon: /*#__PURE__*/_jsx(SvgChevron, {
            orientation: 'right'
          }),
          onClick: () => arrowDateChanges(addDays(internalDate, 1)),
          tabIndex: -1
        })]
      })
    })
  });
};